import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Clock, CheckCircle, XCircle, X, CheckCheck, ArrowRight, AlertTriangle, Edit3, HelpCircle, Ban, Hourglass, Heart } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow } from '../ui/popover';
import { dedupedFetchJson } from '../../lib/requestDedupe';
import { usePoller } from '../../hooks/usePoller';

const API = process.env.REACT_APP_BACKEND_URL;

// Feb 2026 — Real-time leave notifications (rush ticket).
//
// Layered delivery (best to worst case):
//   1. SSE stream (sub-second) — primary path, see GET /api/notifications/stream.
//   2. 30s polling — fallback for proxy hostile networks.
//   3. /unread-count on tab focus — instant catch-up when user returns.
//
// On a new notification we fan out to THREE surfaces:
//   • Bell badge increment + dropdown list refresh
//   • In-app Sonner toast (always — works regardless of permission state)
//   • OS-level Notification (only when user granted permission)
//
// Browser permission is OPT-IN via the inline banner; we never auto-prompt.
const POLL_FALLBACK_MS = 30000;
const SSE_RETRY_BASE_MS = 2000;
const SSE_RETRY_MAX_MS = 30000;
const BROWSER_ALERTS_DISMISSED_KEY = 'titleflow:browser-alerts-banner-dismissed';

const NotificationBell = ({ user }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [detailNotif, setDetailNotif] = useState(null); // WISH-THANKS-NAV-001
  const [browserPermission, setBrowserPermission] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [showAlertsBanner, setShowAlertsBanner] = useState(false);
  // NOTIF-PERSIST-001: queue of actionable popups (newest first). Only the
  // first is rendered; the rest surface as "+N more". Survives SPA
  // navigation (bell lives in the global header); not persisted on refresh.
  const [popupQueue, setPopupQueue] = useState([]);

  // Refs survive renders without retriggering effects
  const eventSourceRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(SSE_RETRY_BASE_MS);
  const seenIdsRef = useRef(new Set());
  const prevUnreadRef = useRef(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  // Decide whether to show the "Enable browser alerts" banner on first open.
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const dismissed = localStorage.getItem(BROWSER_ALERTS_DISMISSED_KEY) === '1';
    if (!dismissed && Notification.permission === 'default') {
      setShowAlertsBanner(true);
    }
  }, []);

  const fetchNotifications = useCallback(async (signal) => {
    try {
      const [countRes, listRes] = await Promise.all([
        dedupedFetchJson(`${API}/api/notifications/unread-count`, { headers: getHeaders(), signal }),
        isOpen ? fetch(`${API}/api/notifications?limit=20`, { headers: getHeaders(), signal }) : Promise.resolve(null)
      ]);
      if (countRes.ok) {
        const newCount = countRes.data?.unread_count || 0;
        const prevCount = prevUnreadRef.current;
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
        // NOTIF-PERSIST-001: polling fallback — when SSE is unavailable and
        // the unread count rises, surface persistent popups for the new
        // unseen unread notifications. Skipped on first load (prevCount null)
        // so popups don't reappear after a full refresh.
        if (prevCount !== null && newCount > prevCount) {
          try {
            const r = await fetch(`${API}/api/notifications?limit=5`, { headers: getHeaders(), signal });
            if (r.ok) {
              const data = await r.json();
              const fresh = (data.notifications || data || [])
                .filter((n) => !n.is_read && !seenIdsRef.current.has(n.id || n._id));
              fresh.reverse().forEach((n) => {
                seenIdsRef.current.add(n.id || n._id);
                setPopupQueue((prev) => [n, ...prev.filter((p) => (p.id || p._id) !== (n.id || n._id))].slice(0, 20));
              });
            }
          } catch { /* noop */ }
        }
      }
      if (listRes && listRes.ok) {
        const listData = await listRes.json();
        const list = listData.notifications || listData || [];
        setNotifications(list);
        // Seed the dedupe ring so SSE pushes for already-fetched notifications
        // don't re-fire toasts when the dropdown opens.
        list.forEach((n) => seenIdsRef.current.add(n.id || n._id));
      }
    } catch (e) { return false; /* silent — poller applies backoff */ }
  }, [getHeaders, isOpen]);

  // ---------- SSE subscription ----------
  // Establishes a persistent EventSource to /api/notifications/stream. On
  // disconnect, exponentially back off retries (capped). The 30s polling
  // fallback below keeps the bell live even if SSE never connects.
  const connectSSE = useCallback(() => {
    if (typeof EventSource === 'undefined') return;       // server-render guard
    if (eventSourceRef.current) return;                   // already connected
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${API}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('hello', () => {
      // Connection established — reset retry backoff
      retryDelayRef.current = SSE_RETRY_BASE_MS;
    });

    es.addEventListener('notification', (ev) => {
      try {
        const notif = JSON.parse(ev.data);
        const nid = notif.id || notif._id;
        if (!nid || seenIdsRef.current.has(nid)) return;  // dedupe vs polling
        seenIdsRef.current.add(nid);

        // 1. Bell — bump count + prepend to list
        setUnreadCount((c) => c + 1);
        setNotifications((prev) => [notif, ...prev.filter((n) => (n.id || n._id) !== nid)].slice(0, 50));

        // 2. Persistent in-app popup (actionable — stays until dismissed)
        fireToast(notif);

        // 3. OS-level browser popup (when permission granted)
        fireBrowserNotification(notif);
      } catch { /* malformed payload — skip */ }
    });

    es.onerror = () => {
      // Browser auto-reconnects on transient errors, but we manage the
      // retry ourselves to apply backoff and avoid hammering an
      // unreachable endpoint.
      try { es.close(); } catch { /* noop */ }
      eventSourceRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        connectSSE();
      }, retryDelayRef.current);
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, SSE_RETRY_MAX_MS);
    };
  }, []);

  // NOTIF-PERSIST-001: bell-feed notifications are actionable — enqueue a
  // persistent popup instead of a transient toast. System feedback toasts
  // (toast.success/error fired by forms) are untouched and still auto-dismiss.
  const fireToast = (notif) => {
    const nid = notif.id || notif._id;
    setPopupQueue((prev) => [notif, ...prev.filter((n) => (n.id || n._id) !== nid)].slice(0, 20));
  };

  const dismissPopup = (nid) => {
    setPopupQueue((prev) => prev.filter((n) => (n.id || n._id) !== nid));
  };

  const handlePopupClick = (notif) => {
    dismissPopup(notif.id || notif._id);
    handleClick(notif);
  };

  // OS-level browser notification (Emergent-style). Only fires if the user
  // granted permission and the tab is not currently focused (otherwise the
  // in-app toast is sufficient and double-notification feels noisy).
  const fireBrowserNotification = (notif) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    try {
      const n = new Notification(notif.title || 'TitleFlow', {
        body: notif.message || '',
        tag: notif.id || notif._id,    // collapse duplicates
        icon: '/favicon.ico',
        badge: '/favicon.ico',
      });
      n.onclick = () => {
        window.focus();
        handleClick(notif);
        n.close();
      };
    } catch { /* permission revoked or browser unsupported */ }
  };

  // Permission prompt — explicit, only fires when user clicks "Enable"
  const requestBrowserPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const result = await Notification.requestPermission();
      setBrowserPermission(result);
      if (result === 'granted') {
        toast.success('Browser alerts enabled', {
          description: 'You will now see desktop notifications for leave events.',
        });
      }
    } catch { /* user dismissed */ }
    setShowAlertsBanner(false);
    localStorage.setItem(BROWSER_ALERTS_DISMISSED_KEY, '1');
  };

  const dismissAlertsBanner = () => {
    setShowAlertsBanner(false);
    localStorage.setItem(BROWSER_ALERTS_DISMISSED_KEY, '1');
  };

  // ---------- Lifecycle: initial fetch + SSE + polling fallback + visibility ----------
  useEffect(() => {
    fetchNotifications();
    connectSSE();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // SSE may have silently dropped while tab was hidden — re-arm.
        // (usePoller below already refetches on tab return.)
        if (!eventSourceRef.current) connectSSE();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch { /* noop */ }
        eventSourceRef.current = null;
      }
    };
  }, [fetchNotifications, connectSSE]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware polling fallback.
  usePoller(fetchNotifications, POLL_FALLBACK_MS, { immediate: false });

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  const markRead = async (notifId) => {
    try {
      await fetch(`${API}/api/notifications/${notifId}/mark-read`, { method: 'POST', headers: getHeaders() });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/notifications/mark-all-read`, { method: 'POST', headers: getHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.id);
    setIsOpen(false);
    // WISH-THANKS-NAV-001 — appreciation notifications have no dedicated page; show the message
    // in a detail popup instead of following the legacy /admin/workspace deep link (→ Leave tab).
    if (isCelebrationThanks(notif)) { setDetailNotif(notif); return; }
    if (notif.action_url) {
      // Feb 25, 2026: SPA navigation via React Router so notifications
      // route inside the app shell (no full reload, preserves auth, etc).
      try {
        const url = notif.action_url.startsWith('http')
          ? new URL(notif.action_url).pathname + new URL(notif.action_url).search
          : notif.action_url;
        navigate(url);
      } catch {
        navigate(notif.action_url);
      }
    }
  };

  const getIcon = (type) => {
    if (type === 'celebration_thanks') return <Heart className="w-4 h-4 text-pink-500" />;
    if (typeof type === 'string' && type.startsWith('probation_')) return <Hourglass className={`w-4 h-4 ${type.includes('overdue') || type.includes('escalation') ? 'text-rose-500' : 'text-violet-500'}`} />; // PROBATION-REVIEW-001 P4
    if (type === 'leave_approved') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    // JCR-NOTIFY-001 — jurisdiction correction lifecycle (Tax Processor)
    if (type === 'jurisdiction_correction_submitted') return <Clock className="w-4 h-4 text-amber-500" />;
    if (type === 'jurisdiction_correction_approved') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (type === 'jurisdiction_correction_rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    if (type === 'leave_rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    if (type === 'leave_cancelled') return <Ban className="w-4 h-4 text-slate-500" />;
    if (type === 'leave_updated') return <Edit3 className="w-4 h-4 text-blue-500" />;
    if (type === 'leave_requires_action') return <HelpCircle className="w-4 h-4 text-amber-500" />;
    if (type && type.startsWith('delivery_exception_')) {
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
    return <Clock className="w-4 h-4 text-blue-500" />;
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const isCelebrationThanks = (n) => n?.type === 'celebration_thanks';
  const thanksTitle = (n) => (/work anniversary/i.test(n?.message || '') ? 'Work anniversary appreciation' : 'Birthday appreciation');

  // JCR-NOTIFY-002 — compact format for jurisdiction-correction notifications:
  // title · "File #: <order>" · Open Order. No jurisdiction details / long message.
  const isCompactFileNotif = (notif) => typeof notif?.type === 'string' && notif.type.startsWith('jurisdiction_correction_');
  const fileLine = (notif) => (notif?.order_number ? `File #: ${notif.order_number}` : null);

  const isUnread = (notif) => {
    if (typeof notif.is_read === 'boolean') return !notif.is_read;
    if (Array.isArray(notif.read_by) && user?.id) return !notif.read_by.includes(user.id);
    return false;
  };

  const activePopup = popupQueue[0];
  const queuedMore = Math.max(0, popupQueue.length - 1);

  return (
    <>
      {/* WISH-THANKS-NAV-001 — appreciation detail popup (no dedicated page) */}
      <Dialog open={!!detailNotif} onOpenChange={(o) => !o && setDetailNotif(null)}>
        <DialogContent className="sm:max-w-md" data-testid="celebration-thanks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900"><Heart className="w-5 h-5 text-pink-500" />{detailNotif ? thanksTitle(detailNotif) : ''}</DialogTitle>
            <DialogDescription className="sr-only">Appreciation message</DialogDescription>
          </DialogHeader>
          {detailNotif && (
            <div className="space-y-3">
              <p className="text-sm text-slate-800 leading-relaxed" data-testid="celebration-thanks-message">{detailNotif.message}</p>
              <p className="text-xs text-slate-500">
                {detailNotif.created_by_name ? `From ${detailNotif.created_by_name} · ` : ''}{timeAgo(detailNotif.created_at) || 'just now'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* NOTIF-PERSIST-001: persistent actionable popup — below header, top-right */}
      {activePopup && (
        <div
          className="fixed top-[72px] right-4 z-[70] w-[380px] max-w-[calc(100vw-2rem)]"
          data-testid="persistent-notification-popup"
        >
          <div className="bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden">
            <div className="flex items-start gap-3 p-4">
              <div className="mt-0.5 flex-shrink-0">{getIcon(activePopup.type || activePopup.notification_type)}</div>
              <button
                onClick={() => activePopup.action_url ? handlePopupClick(activePopup) : dismissPopup(activePopup.id || activePopup._id)}
                className="flex-1 min-w-0 text-left group"
                data-testid="popup-body-btn"
              >
                <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-700">
                  {activePopup.title || 'New notification'}
                </p>
                {/* explicit color: the popup lives inside .header-blue, whose theme repaints .text-slate-600 white */}
                {isCompactFileNotif(activePopup) ? (
                  <p className="text-sm mt-0.5" style={{ color: '#475569' }} data-testid="popup-file-number">{fileLine(activePopup) || activePopup.message}</p>
                ) : (
                  <p className="text-sm mt-0.5 line-clamp-3" style={{ color: '#475569' }}>{activePopup.message}</p>
                )}
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  {timeAgo(activePopup.created_at) || 'just now'}
                  {activePopup.action_url && (
                    <span className="flex items-center gap-0.5 text-blue-600 group-hover:underline ml-1">
                      {isCompactFileNotif(activePopup) ? 'Open Order' : 'Open'} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </p>
              </button>
              <button
                onClick={() => dismissPopup(activePopup.id || activePopup._id)}
                aria-label="Dismiss notification popup"
                title="Dismiss (stays unread in the bell)"
                data-testid="popup-close-btn"
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {queuedMore > 0 && (
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-100">
                <button
                  onClick={() => setIsOpen(true)}
                  data-testid="popup-more-indicator"
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  +{queuedMore} more notification{queuedMore > 1 ? 's' : ''}
                </button>
                <button
                  onClick={() => setPopupQueue([])}
                  data-testid="popup-dismiss-all"
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Dismiss all
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all"
          data-testid="notification-bell-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm" data-testid="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={10}
        className="w-96 p-0 rounded-xl shadow-2xl border border-slate-200"
        data-testid="notification-dropdown"
      >
        <PopoverArrow className="fill-white" style={{ filter: 'drop-shadow(0 -1px 0 rgb(226 232 240))' }} width={14} height={7} />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t-xl">
          <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Inline "Enable browser alerts" banner — Feb 2026.
            Shown once until the user enables or dismisses. Never auto-prompts
            (user-direction: "non-intrusive inline banner, not forced prompt").  */}
        {showAlertsBanner && browserPermission === 'default' && (
          <div
            className="flex items-start gap-2 px-4 py-2.5 bg-indigo-50 border-b border-indigo-100"
            data-testid="enable-browser-alerts-banner"
          >
            <Bell className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-indigo-900">Enable desktop alerts</p>
              <p className="text-[11px] text-indigo-700 leading-tight mt-0.5">
                Get notified about leave events even when TitleFlow is in the background.
              </p>
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  onClick={requestBrowserPermission}
                  className="text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2 py-1 transition-colors"
                  data-testid="enable-browser-alerts-btn"
                >
                  Enable
                </button>
                <button
                  onClick={dismissAlertsBanner}
                  className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800"
                  data-testid="dismiss-browser-alerts-btn"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-10 text-center">
              <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const unread = isUnread(notif);
              return (
                <div
                  key={notif.id || notif._id}
                  onClick={() => handleClick(notif)}
                  className={`flex gap-3 px-4 py-3 cursor-pointer border-b border-slate-50 transition-colors ${
                    unread ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {getIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-tight ${unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                        {notif.title || notif.message?.split('.')[0] || 'Notification'}
                      </p>
                      {unread && <span className="mt-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2" data-testid={isCompactFileNotif(notif) ? `notification-file-${notif.id}` : undefined}>
                      {isCompactFileNotif(notif) ? (fileLine(notif) || notif.message) : notif.message}
                    </p>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                      <p className="text-[10px] text-slate-400">{timeAgo(notif.created_at)}</p>
                      {notif.action_url && notif.cta_label && (
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                            notif.type && notif.type.startsWith('delivery_exception_')
                              ? 'text-amber-700'
                              : 'text-indigo-600'
                          }`}
                          data-testid={`notification-cta-${notif.id}`}
                        >
                          {notif.cta_label}
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
};

export default NotificationBell;
